import { useState, useEffect } from 'react';
import { Pencil, Check, X, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../lib/format.js';
import { useUpdateLotAcquisition } from '../hooks/useLots.js';

const VAT_REGIMES = [
  { value: 'particulier',        label: 'Particulier' },
  { value: 'pro_non_assujetti',  label: 'Pro non assujetti' },
  { value: 'pro_assujetti',      label: 'Pro assujetti' },
];

const VAT_LABELS = Object.fromEntries(VAT_REGIMES.map((r) => [r.value, r.label]));

const DOC_OPTIONS = [
  { value: true,  label: 'Oui' },
  { value: false, label: 'Non' },
  { value: null,  label: 'Inconnu' },
];

function docLabel(v) {
  return DOC_OPTIONS.find((o) => o.value === v)?.label ?? 'Inconnu';
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    cost_ht_euros:    '',
    cost_ttc_euros:   '',
    acquisition_date: todayISO(),
    supplier_name:    '',
    vat_regime:       'pro_assujetti',
    description:      '',
    payment_method:   '',
    has_accounting_document: null,
  };
}

function fromAcquisition(a) {
  return {
    cost_ht_euros:    (a.cost_ht_cents / 100).toFixed(2),
    cost_ttc_euros:   (a.cost_ttc_cents / 100).toFixed(2),
    acquisition_date: a.acquisition_date ?? '',
    supplier_name:    a.supplier_name,
    vat_regime:       a.vat_regime,
    description:      a.description ?? '',
    payment_method:   a.payment_method ?? '',
    has_accounting_document: a.has_accounting_document ?? null,
  };
}

function parseEuros(str) {
  if (str == null || str === '') return NaN;
  const cleaned = String(str).replace(/\s/g, '').replace(',', '.');
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : NaN;
}

export default function LotAcquisitionPanel({ lotNumber, acquisition }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() =>
    acquisition ? fromAcquisition(acquisition) : emptyForm()
  );
  const [errorMsg, setErrorMsg] = useState(null);

  const mutation = useUpdateLotAcquisition();

  useEffect(() => {
    if (acquisition) setForm(fromAcquisition(acquisition));
  }, [acquisition]);

  function startEdit() {
    setForm(acquisition ? fromAcquisition(acquisition) : emptyForm());
    setErrorMsg(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setErrorMsg(null);
  }

  function handleChange(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function save() {
    setErrorMsg(null);
    const ht = parseEuros(form.cost_ht_euros);
    const ttc = parseEuros(form.cost_ttc_euros);

    if (!Number.isFinite(ht) || ht < 0) return setErrorMsg('Coût HT invalide');
    if (!Number.isFinite(ttc) || ttc < 0) return setErrorMsg('Coût TTC invalide');
    if (ttc < ht) return setErrorMsg('Le TTC ne peut pas être inférieur au HT');
    if (!form.supplier_name?.trim()) return setErrorMsg('Nom du fournisseur requis');

    try {
      await mutation.mutateAsync({
        lotNumber,
        body: {
          cost_ht_cents:    ht,
          cost_ttc_cents:   ttc,
          acquisition_date: form.acquisition_date || null,
          supplier_name:    form.supplier_name.trim(),
          vat_regime:       form.vat_regime,
          description:      form.description.trim() || null,
          payment_method:   form.payment_method.trim() || null,
          has_accounting_document: form.has_accounting_document,
        },
      });
      setEditing(false);
    } catch (err) {
      setErrorMsg(err?.message ?? 'Erreur d\'enregistrement');
    }
  }

  // ── Display: rien saisi ───────────────────────────────────────────────────
  if (!acquisition && !editing) {
    return (
      <div className="bg-white rounded-md border border-slate-200 px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-1">Acquisition</h3>
        <p className="text-xs text-slate-500 mb-3">
          Le coût d'acquisition n'est pas encore saisi pour ce lot.
        </p>
        <button
          type="button"
          onClick={startEdit}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-700 transition-colors"
        >
          <Pencil size={13} aria-hidden /> Saisir le coût d'acquisition
        </button>
      </div>
    );
  }

  // ── Edit form ────────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div className="bg-white rounded-md border border-slate-200 px-4 py-4">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">Acquisition — saisie</h3>
          <button
            type="button"
            onClick={cancelEdit}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            disabled={mutation.isPending}
          >
            <X size={13} aria-hidden /> Annuler
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Field label="Coût HT (€)">
            <input
              type="text"
              inputMode="decimal"
              value={form.cost_ht_euros}
              onChange={(e) => handleChange('cost_ht_euros', e.target.value)}
              placeholder="1234.50"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Field>
          <Field label="Coût TTC (€)">
            <input
              type="text"
              inputMode="decimal"
              value={form.cost_ttc_euros}
              onChange={(e) => handleChange('cost_ttc_euros', e.target.value)}
              placeholder="1481.40"
              className="w-full px-2 py-1.5 border border-slate-300 rounded text-right tabular-nums focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Field>
          <Field label="Date facture (optionnelle)">
            <input
              type="date"
              value={form.acquisition_date}
              onChange={(e) => handleChange('acquisition_date', e.target.value)}
              className="w-full px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Field>
          <Field label="Fournisseur">
            <input
              type="text"
              value={form.supplier_name}
              onChange={(e) => handleChange('supplier_name', e.target.value)}
              maxLength={200}
              placeholder="Nom du vendeur"
              className="w-full px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Field>
          <Field label="Description">
            <input
              type="text"
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              maxLength={300}
              placeholder="Modèle moto, nature du lot…"
              className="w-full px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Field>
          <Field label="Moyen de paiement">
            <input
              type="text"
              value={form.payment_method}
              onChange={(e) => handleChange('payment_method', e.target.value)}
              maxLength={100}
              placeholder="Virement, espèces, CB…"
              className="w-full px-2 py-1.5 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          </Field>
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Document comptable</p>
            <div className="inline-flex items-center rounded-md border border-slate-300 p-0.5 text-xs">
              {DOC_OPTIONS.map((o) => (
                <button
                  key={o.label}
                  type="button"
                  onClick={() => handleChange('has_accounting_document', o.value)}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    form.has_accounting_document === o.value
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  aria-pressed={form.has_accounting_document === o.value}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Régime TVA</p>
            <div className="inline-flex items-center rounded-md border border-slate-300 p-0.5 text-xs">
              {VAT_REGIMES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => handleChange('vat_regime', r.value)}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    form.vat_regime === r.value
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                  aria-pressed={form.vat_regime === r.value}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {errorMsg && (
          <p className="mt-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
            {errorMsg}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {mutation.isPending
              ? <Loader2 size={13} className="animate-spin" aria-hidden />
              : <Check size={13} aria-hidden />}
            Enregistrer
          </button>
        </div>
      </div>
    );
  }

  // ── Display: acquisition saisie ───────────────────────────────────────────
  const recoveryPct = acquisition.cost_recovery_pct;
  const recoveryClamped = Math.min(100, recoveryPct ?? 0);
  const profitable = recoveryPct != null && recoveryPct >= 100;

  return (
    <div className="bg-white rounded-md border border-slate-200 px-4 py-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Acquisition</h3>
        <button
          type="button"
          onClick={startEdit}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <Pencil size={12} aria-hidden /> Modifier
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
        <DisplayField label="Coût HT" value={formatCurrency(acquisition.cost_ht_cents)} bold />
        <DisplayField label="Coût TTC" value={formatCurrency(acquisition.cost_ttc_cents)} />
        <DisplayField label="Date facture" value={formatDate(acquisition.acquisition_date)} />
        <DisplayField label="Régime TVA" value={VAT_LABELS[acquisition.vat_regime] ?? acquisition.vat_regime} />
        <div className="col-span-2">
          <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">Fournisseur</p>
          <p className="text-sm text-slate-800">{acquisition.supplier_name}</p>
        </div>
        <div className="col-span-2">
          <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">Description</p>
          <p className="text-sm text-slate-800">{acquisition.description ?? '—'}</p>
        </div>
        <DisplayField label="Moyen de paiement" value={acquisition.payment_method ?? '—'} />
        <DisplayField label="Document comptable" value={docLabel(acquisition.has_accounting_document)} />
      </div>

      {recoveryPct != null && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="flex items-baseline justify-between mb-1.5">
            <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">Amortissement</p>
            <p className={`text-base font-bold tabular-nums ${profitable ? 'text-emerald-700' : 'text-slate-900'}`}>
              {recoveryPct} %
            </p>
          </div>
          <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
            <div
              className={`h-full ${profitable ? 'bg-emerald-500' : 'bg-slate-400'}`}
              style={{ width: `${recoveryClamped}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-slate-400 tabular-nums">
            {profitable ? 'Lot rentabilisé' : `Reste ${100 - recoveryPct} % avant amortissement complet`}
          </p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function DisplayField({ label, value, bold }) {
  return (
    <div>
      <p className="text-[11px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`mt-0.5 tabular-nums ${bold ? 'text-base font-semibold text-slate-900' : 'text-sm text-slate-800'}`}>
        {value}
      </p>
    </div>
  );
}
